import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Component, HostBinding, HostListener, Input, OnDestroy, OnInit } from "@angular/core";
import { TranslationService } from "@services/translation.service";
import { Subscription, timer } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import "./site-status-indicator.component.scss";

export interface IBetterUptimeResponse {
    id: string;
    type: string;
    attributes: IBetterUptimeResponseAttributes;
}

export interface IBetterUptimeResponseAttributes {
    aggregate_state: BetterUptimeStatus;
    announcement: string;
}

declare type BetterUptimeStatus = "operational" | "degraded" | "downtime";

@Component({
    selector: "site-status-indicator",
    template: require("./site-status-indicator.component.html")
})
export class SiteStatusIndicatorComponent implements OnInit, OnDestroy {

    @Input() darkBackground: boolean;
    @HostBinding("class") class: string;
    @HostListener("click") onClick() { window.open(this.statusPageUrl, '_blank').focus(); }

    // ---

    status: BetterUptimeStatus;
    statusPageUrl = "https://status.verilocation.com/";
    statusMessage: string;
    statusIcon: string;
    announcement: string;

    subscriptions = new Subscription();

    constructor(private http: HttpClient,
        private readonly translationService: TranslationService) {
    }

    ngOnInit() {
        this.startMonitoring();
    }

    ngOnDestroy() {
        this.subscriptions?.unsubscribe();
    }

    // ---

    private startMonitoring() {

        // handle data and handle error
        const dataObservable = this.getStatus()
            .pipe(map(data => this.processStatusUpdate(data)))
            .pipe(catchError(error => this.handleError(error)));

        // switch to new stream, separating the error handling on data observable
        const intervalObservable = timer(0, 1 * 60 * 1000)
            .pipe(switchMap(() => dataObservable));

        this.subscriptions.add(intervalObservable.subscribe());
    }

    private getStatus() {
        return this.http.get<IBetterUptimeResponse>("/api/servicestatus/new");
    }

    private processStatusUpdate(update: IBetterUptimeResponse) {
        this.updateStatus(update.attributes.aggregate_state);

        this.announcement = update.attributes.announcement;
    }

    private updateStatus(status: BetterUptimeStatus) {
        this.status = status;

        switch (this.status) {

            case "operational":
                this.statusMessage = this.translationService.t("SiteStatus.AllOnline");
                this.statusIcon = "check_circle";
                break;

            case "degraded":
                this.statusMessage = this.translationService.t("SiteStatus.Issues");
                this.statusIcon = "warning";
                break;

            case "downtime":
                this.statusMessage = this.translationService.t("SiteStatus.Down");
                this.statusIcon = "error";
        }

        this.class = status;
        if (this.darkBackground) {
            this.class += " dark-background";
        }
    }

    private handleError(err: HttpErrorResponse): any[] {
        if (err.error.indexOf("Received (502) BadGateway") !== -1) {
            this.updateStatus("operational");
        } else {
            this.updateStatus("downtime");
        }

        return [];
    }
}
